<template>
  <div class="fee-panel" :class="{ compact, inline, 'inline-refund': inline && mode === 'refund' }">
    <template v-if="inline">
      <div v-if="title" class="fee-panel-title">{{ title }}</div>
      <div class="fee-inline-grid">
        <div
          v-for="row in displayRows"
          :key="row.key"
          class="fee-inline-item"
        >
          <span class="label">{{ row.label }}</span>
          <Tooltip
            v-if="row.tooltip"
            :title="row.tooltip"
            overlay-class-name="fee-type-breakdown-tooltip"
          >
            <span class="value value--hoverable" :class="row.highlight">{{ row.value }}</span>
          </Tooltip>
          <span v-else class="value" :class="row.highlight">{{ row.value }}</span>
        </div>
      </div>
    </template>
    <template v-else>
      <div v-if="title" class="fee-panel-title">{{ title }}</div>
      <table class="fee-table">
        <tbody>
          <tr v-for="row in displayRows" :key="row.key">
            <td class="label">{{ row.label }}</td>
            <td class="value" :class="row.highlight">{{ row.value }}</td>
          </tr>
        </tbody>
      </table>
    </template>
    <div v-if="showDisclaimer" class="disclaimer">
      以上费用来源于铁路供应商预估查询，实际核收以办理时系统计算为准。
      <a class="rules-link" @click.prevent="emit('show-rules')">查看退改规则</a>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { Tooltip } from 'ant-design-vue'
import { formatYuan } from '@/utils/money'

const props = defineProps({
  title: { type: String, default: '费用明细' },
  /** refund | reschedule */
  mode: { type: String, default: 'refund' },
  ticketPriceYuan: { type: Number, default: 0 },
  refundFeeYuan: { type: Number, default: 0 },
  refundServiceFeeYuan: { type: Number, default: 0 },
  refundAmountYuan: { type: Number, default: 0 },
  rescheduleFeeYuan: { type: Number, default: 0 },
  rescheduleServiceFeeYuan: { type: Number, default: 0 },
  priceDiffYuan: { type: Number, default: 0 },
  totalPayYuan: { type: Number, default: null },
  showDisclaimer: { type: Boolean, default: true },
  compact: { type: Boolean, default: false },
  /** 改签费用与明细同一行展示 */
  inline: { type: Boolean, default: false },
  /** 按乘客类型的人数明细（悬浮提示） */
  typeFeeBreakdown: { type: Object, default: null },
  /** 未勾选乘车人时，费用行展示 -- */
  pendingSelection: { type: Boolean, default: false }
})

const emit = defineEmits(['show-rules'])

const formatMoney = (amount) => `¥ ${formatYuan(amount)}`

const formatRefundFee = (amount) => {
  if (amount <= 0) return '免收'
  return formatMoney(amount)
}

const displayRows = computed(() => {
  if (props.mode === 'reschedule') {
    const diff = Number(props.priceDiffYuan) || 0
    const breakdown = props.typeFeeBreakdown || {}
    const rows = [
      {
        key: 'ticketPrice',
        label: '原出票价',
        value: formatMoney(props.ticketPriceYuan),
        highlight: '',
        tooltip: breakdown.ticketPrice || ''
      },
      {
        key: 'rescheduleFee',
        label: '改签手续费',
        value:
          props.rescheduleFeeYuan <= 0
            ? '免收'
            : `¥ ${formatYuan(props.rescheduleFeeYuan)}`,
        highlight: props.rescheduleFeeYuan > 0 ? 'warn' : 'success',
        tooltip: breakdown.rescheduleFee || ''
      },
      {
        key: 'rescheduleServiceFee',
        label: '改签服务费',
        value:
          props.rescheduleServiceFeeYuan <= 0
            ? '免收'
            : `¥ ${formatYuan(props.rescheduleServiceFeeYuan)}`,
        highlight: props.rescheduleServiceFeeYuan > 0 ? 'warn' : 'success',
        tooltip: breakdown.rescheduleServiceFee || ''
      },
      {
        key: 'priceDiff',
        label: '票款差额',
        value: `¥ ${formatYuan(Math.abs(diff))}`,
        highlight: diff > 0 ? 'warn' : diff < 0 ? 'success' : '',
        tooltip: breakdown.priceDiff || ''
      },
      {
        key: 'totalPay',
        label: diff >= 0 ? '总计应补' : '总计应退',
        value: formatMoney(
          Math.abs(
            props.totalPayYuan != null
              ? props.totalPayYuan
              : (Number(props.rescheduleFeeYuan) || 0)
                  + (Number(props.rescheduleServiceFeeYuan) || 0)
                  + (Number(props.priceDiffYuan) || 0)
          )
        ),
        highlight: 'primary',
        tooltip: breakdown.totalPay || ''
      }
    ]
    return rows
  }

  return [
    {
      key: 'ticketPrice',
      label: '原出票价',
      value: props.pendingSelection ? '--' : formatMoney(props.ticketPriceYuan),
      highlight: ''
    },
    {
      key: 'fee',
      label: '收取退票费',
      value: props.pendingSelection ? '--' : formatRefundFee(props.refundFeeYuan),
      highlight: props.pendingSelection ? '' : props.refundFeeYuan > 0 ? 'warn' : 'success'
    },
    {
      key: 'serviceFee',
      label: '收取退票服务费',
      value: props.pendingSelection ? '--' : formatRefundFee(props.refundServiceFeeYuan),
      highlight:
        props.pendingSelection ? '' : props.refundServiceFeeYuan > 0 ? 'warn' : 'success'
    },
    {
      key: 'refund',
      label: '应退金额',
      value: props.pendingSelection ? '--' : formatMoney(props.refundAmountYuan),
      highlight: props.pendingSelection ? '' : 'primary'
    }
  ]
})
</script>

<style lang="scss" scoped>
.fee-panel {
  border: 1px solid #e8e0c8;
  border-radius: 4px;
  background: linear-gradient(180deg, #fffdf5 0%, #fff 40%);
  padding: 14px 16px 12px;
}

.fee-panel.compact {
  padding: 10px 12px;
}

.fee-panel-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
  padding-left: 8px;
  border-left: 3px solid #ff8001;
}

.fee-panel.inline {
  padding: 16px 20px 14px;
}

.fee-panel.inline .fee-panel-title {
  margin-bottom: 10px;
  font-size: 14px;
}

.fee-panel.inline.inline-refund .fee-inline-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.fee-panel.inline.inline-refund {
  padding: 12px 14px 10px;
}

.fee-panel.inline.inline-refund .disclaimer {
  margin-top: 10px;
  padding-top: 8px;
}

.fee-inline-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  column-gap: 12px;
  align-items: center;
  width: 100%;
}

.fee-inline-item {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
  white-space: nowrap;

  .label {
    color: #666;
    font-size: 14px;
    line-height: 1.4;
    flex-shrink: 0;
  }

  .value {
    font-weight: 600;
    color: #333;
    font-size: 16px;
    line-height: 1.2;

    &.warn {
      color: #ff8001;
    }

    &.success {
      color: #389e0d;
    }

    &.primary {
      color: #1d4ed8;
      font-size: 18px;
      font-weight: 700;
    }

    &.value--hoverable {
      cursor: pointer;
      border-bottom: 1px dashed currentColor;
    }
  }
}

.fee-panel.inline .disclaimer {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px dashed #eee4d0;
}

.fee-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  td {
    padding: 8px 4px;
    border-bottom: 1px dashed #eee4d0;
    vertical-align: middle;
  }

  tr:last-child td {
    border-bottom: none;
    padding-top: 10px;
  }

  .label {
    color: #666;
    width: 42%;
  }

  .value {
    text-align: right;
    font-weight: 600;
    color: #333;
    font-size: 15px;

    &.warn {
      color: #ff8001;
    }

    &.success {
      color: #389e0d;
    }

    &.primary {
      color: #1d4ed8;
      font-size: 16px;
    }
  }
}

.disclaimer {
  margin-top: 10px;
  font-size: 12px;
  color: #999;
  line-height: 1.7;
}

.rules-link {
  margin-left: 6px;
  color: #1d4ed8;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}
</style>

<style lang="scss">
.fee-type-breakdown-tooltip .ant-tooltip-inner {
  white-space: pre-line;
  text-align: left;
  min-width: 120px;
}
</style>
